import authService from './authService';
import SecureCookieManager from '../utils/secureCookie';

let BASE_URL = process.env.REACT_APP_API_URL;

async function loadConfig() {
  try {
    const response = await fetch("/config.json");
    const config = await response.json();
    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    console.error("Failed to load config.json. Using default .env API URL", error);
  }
}

loadConfig().then(() => {});

export const getInformationUserRegister = async (formData: any) => {
  try {
    const response = await fetch(
      "https://n8nio.pianat.ai/webhook/d6cb60ba-7b15-4a7f-b7a2-34fb27e4519d",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_data: formData }),
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to fetch company information");
    }
    const result = await response.json();
    if (!result[0]) {
      throw new Error("Invalid response structure");
    }
    return { success: true, companyData: result[0] };
  } catch (error: any) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unexpected error during company info fetch",
    };
  }
};

export interface ApiObjective {
  objective_id: string;
  objective_name: string;
  description: string;
  estimated_effort: string;
  priority_rank: number;
  regulatory_reference: string;
  suggested_start_date: string;
  suggested_completion_date: string;
}

export interface CompanyData {
  organization_name: string;
  headquarters_country: string;
  industry_sectors: string[];
  entity_type: string;
  key_services: string[];
  company_size: {
    employees: number | null;
    revenue_band: string;
  };
  applicable_compliance_frameworks?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

export interface ApiResponse {
  success: boolean;
  data?: {
    roadmap?: ApiObjective[];
    data_sources?: string[];
    notes?: string;
  };
  message?: string;
}

function getTokenForN8nWebhook(): string | null {
  if (typeof window === "undefined") return null;
  const fromCookie = SecureCookieManager.getAuthToken();
  if (fromCookie) return fromCookie;
  return localStorage.getItem("token");
}

export const getObjectiveInfoRegister = async (formData: any): Promise<ApiResponse> => {
  try {
    const token = getTokenForN8nWebhook();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const response = await fetch("https://n8nio.pianat.ai/webhook/advisor_agent_eg", {
      method: "POST",
      headers,
      body: JSON.stringify({ data: formData }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to fetch objectives");
    }
    const result = await response.json();
    if (Array.isArray(result) && result.length > 0 && result[0].roadmap) {
      return {
        success: true,
        data: {
          roadmap: result[0].roadmap,
          data_sources: result[0].data_sources || [],
          notes: result[0].notes || "",
        },
      };
    }
    throw new Error("Invalid response structure");
  } catch (error: any) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unexpected error during objectives fetch",
    };
  }
};

export async function submitObjectiveInfo(token: string, data: any[]): Promise<any> {
  const response = await authService.apiCall(
    `${BASE_URL}/registration-steps/objective-info?token=${token}`,
    { method: "POST", body: JSON.stringify({ data }) }
  );
  const result = await response.json();
  if (result.isSuccess && result.data?.token) {
    localStorage.setItem("token", result.data.token);
  }
  return result;
}

export async function getInformationUser() {
  try {
    const response = await authService.apiCall(`${BASE_URL}/information/user`, { method: 'GET' });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to fetch user information');
    }
    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching user information:', error);
    throw new Error(error?.message || 'Error fetching user information');
  }
}

/** Re-run advisor AI with latest user profile and persist objectives (e.g. after assessment answers change). */
export async function regenerateObjectivesFromUserProfile(): Promise<{ success: boolean; message?: string }> {
  try {
    const companyInfo = await getInformationUser();
    const response = await getObjectiveInfoRegister(companyInfo);
    if (!response.success || !response.data?.roadmap) {
      return {
        success: false,
        message: response.message || 'Advisor did not return objectives.',
      };
    }
    const aiResponseData = [
      {
        roadmap: response.data.roadmap,
        data_sources: response.data.data_sources || [],
        notes: response.data.notes || '',
      },
    ];
    return await saveObjectivesToBackend(aiResponseData);
  } catch (error: any) {
    console.error('regenerateObjectivesFromUserProfile:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to regenerate objectives',
    };
  }
}

export async function submitComplianceByFramwork(data: any) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/compliance/bulk`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to submit answers. Status: ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error submitting answers:', error);
    throw new Error(error?.message || 'Error submitting answers');
  }
}

export async function getMappedObjectives(data: any) {
  try {
    const response = await fetch("https://n8nio.pianat.ai/webhook/60f7bc39-248f-4b01-a3ab-bb28d2e1308f", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data }),
    });
    if (!response.ok) {
      throw new Error(`Mapper Agent failed with status: ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error("Error calling Mapper Agent:", error);
    throw error;
  }
}

export async function mapControls(data: { applicable_frameworks: string[] }) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/controls/mapper`, {
      method: 'POST',
      body: JSON.stringify({ applicable_frameworks: data.applicable_frameworks }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error mapping controls:', error);
    throw new Error(error?.message || 'Error mapping controls');
  }
}

export async function getObjectivesFromBackend(): Promise<any[] | null> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/objectives`, { method: 'GET' });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    if (result.success && result.data?.data && Array.isArray(result.data.data) && result.data.data.length > 0) {
      return result.data.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching objectives from backend:', error);
    return null;
  }
}

export async function saveObjectivesToBackend(data: any[]): Promise<{ success: boolean; message?: string }> {
  try {
    if (!Array.isArray(data) || data.length === 0) {
      return { success: false, message: 'Invalid data format. Expected a non-empty array.' };
    }
    const response = await authService.apiCall(`${BASE_URL}/objectives`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    return { success: result.success || false, message: result.message };
  } catch (error: any) {
    console.error('Error saving objectives to backend:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to save objectives',
    };
  }
}

export async function getControlMapperFromBackend(): Promise<any | null> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/control-mapper`, { method: 'GET' });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    if (result.success && result.data?.data && typeof result.data.data === 'object') {
      const d = result.data.data;
      if (
        (d.individual && Array.isArray(d.individual)) ||
        (d.grouped && Array.isArray(d.grouped))
      ) {
        return d;
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching control mapper from backend:', error);
    return null;
  }
}

export async function saveControlMapperToBackend(data: {
  individual?: any[];
  grouped?: any[];
}): Promise<{ success: boolean; message?: string }> {
  try {
    if (!data || typeof data !== 'object') {
      return {
        success: false,
        message: 'Invalid data format. Expected an object with individual and/or grouped arrays.',
      };
    }
    if (
      (!data.individual || !Array.isArray(data.individual) || data.individual.length === 0) &&
      (!data.grouped || !Array.isArray(data.grouped) || data.grouped.length === 0)
    ) {
      return {
        success: false,
        message: 'Invalid data format. Expected at least one non-empty array (individual or grouped).',
      };
    }
    const response = await authService.apiCall(`${BASE_URL}/control-mapper`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    return { success: result.success || false, message: result.message };
  } catch (error: any) {
    console.error('Error saving control mapper to backend:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to save control mapper',
    };
  }
}
